<template>
  <v-text-field
    v-tooltip:top="getTooltipDisplayValue(formattedValue)"
    :model-value="formattedValue"
    :label="label"
    :rules="ruleList"
    :disabled="disabled"
    :validate-on="validateOn"
    placeholder="MM/DD/YYYY"
    @keydown.enter="onBlur"
    @blur="onBlur"
    @update:model-value="onUpdateDateText">
    <template #append-inner>
      <v-icon :id="`date-input-${id}-icon`" icon="mdi-calendar" />
    </template>
  </v-text-field>
  <v-menu :activator="`#date-input-${id}-icon`" :disabled="disabled">
    <v-list>
      <v-list-item @click.stop>
        <v-date-picker v-model="value" :max="props.maxDate" @update:model-value="emit('update:modelValue', $event)">
          <template #actions>
            <v-btn text @click="onClear">Clear</v-btn>
          </template>
        </v-date-picker>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script setup>
import { ref, computed, watch, onMounted, useId } from "vue";
import { useFieldTooltip } from "@/composables/useFieldTooltip";

const { getTooltipDisplayValue } = useFieldTooltip();

const props = defineProps({
  modelValue: {
    type: [Date, null],
    required: false,
    default: null,
  },
  label: {
    type: String,
    required: false,
    default: "",
  },
  rules: {
    type: Array,
    required: false,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  validateOn: {
    type: String,
    required: false,
    default: "eager",
  },
  maxDate: {
    type: Date,
    required: false,
    default: () => new Date(),
  },
});
const emit = defineEmits(["update:modelValue"]);

const validDateRule = (v) => {
  if (!v) return true;
  const date = getDateFromText(v);
  if (date === null) {
    return "Invalid date";
  }
  if (props.maxDate && date > props.maxDate) {
    return "Date cannot be in the future";
  }
  return true;
};

const id = useId();
const value = ref(props.modelValue);
const formattedValue = ref("");
const ruleList = computed(() => {
  return [...props.rules, validDateRule];
});

onMounted(() => {
  if (props.modelValue) {
    formattedValue.value = formatDate(props.modelValue);
    value.value = props.modelValue;
  }
});

// Keep the values updated as the incoming value updates
watch(
  () => props.modelValue,
  (newVal) => {
    formattedValue.value = newVal ? formatDate(newVal) : "";
    value.value = newVal;
  }
);

/**
 * Only return a date if the text is a valid date, in the format MM/DD/YYYY.
 * Otherwise, return null.
 */
const getDateFromText = (text) => {
  const parts = text.split("/");
  if (parts.length !== 3 || parts.some((p) => !p || isNaN(p))) {
    return null;
  }
  const [monthStr, dayStr, yearStr] = parts;
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);
  const year = parseInt(yearStr, 10);

  // Validate ranges
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > 9999) return null;

  const d = new Date(year, month - 1, day);
  if (isNaN(d.getTime())) {
    return null;
  }

  // Verify no overflow (e.g., Feb 30 → Mar 2)
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return null;
  }
  return d;
};

/**
 * Update the formatted value and the value when the user types in the text field.
 * We only emit the update on blur
 */
const onUpdateDateText = (newVal) => {
  const slashCount = (newVal.match(/\//g) || []).length;
  if (newVal.length === 2 && slashCount === 0) {
    newVal = newVal + "/";
  } else if (newVal.length === 5 && slashCount === 1) {
    newVal = newVal + "/";
  }
  if (newVal.length > 10) {
    newVal = newVal.slice(0, 10);
  }
  formattedValue.value = newVal;
};

/**
 * Update the value when the user blurs the text field.
 */
const onBlur = () => {
  if (formattedValue.value === "" && value.value !== null) {
    // If the user clears the date, set the value to null
    value.value = null;
    formattedValue.value = "";
    emit("update:modelValue", null);
    return;
  }
  const date = getDateFromText(formattedValue.value);
  const currentValid = props.modelValue instanceof Date && !Number.isNaN(props.modelValue.getTime());
  const isDifferent = !date ? currentValid : !currentValid || date.toISOString() !== props.modelValue.toISOString();
  if (date && isDifferent) {
    value.value = date;
    formattedValue.value = formatDate(date);
    emit("update:modelValue", date);
  }
};

/**
 * Format the date as MM/DD/YYYY.
 */
const formatDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }
  return (
    `${(date.getMonth() + 1).toString().padStart(2, "0")}/` +
    `${date.getDate().toString().padStart(2, "0")}/` +
    `${date.getFullYear()}`
  );
};

const onClear = () => {
  value.value = null;
  formattedValue.value = "";
  emit("update:modelValue", null);
};
</script>
